import { createContext, useContext, useState } from 'react';

const initialState = {
  name: '',
  email: '',
  oid: '',
  image: '',
  tokenId: '',
  status: '',
  site_tour: '',
  action: null,
  canceled: false,
  start_date: null,
  current_period_start: null,
  current_period_end: null,
  book_tokens: 0,
  script_tokens: 0,
  extra_book_tokens: 0,
  extra_script_tokens: 0,
  promo_book_tokens: 0,
  promo_script_tokens: 0,
  promo_expiration_date: null,
  custom_script_template: null,
  custom_book_template: null,
  show_chat: false,
  company_id: '',
  company_name: '',
  isIndividualUser: true
};

export const UserDetailsContext = createContext({
  userDetails: initialState,
  updateUserDetails: () => {},
  updateUserTokens: () => {}
});

export const useUserDetialsContext = () => useContext(UserDetailsContext);

/* eslint-disable react/prop-types */
export function UserDetailsContextProvider({ children }) {
  const [userDetails, setUserDetails] = useState(initialState);

  const updateUserDetails = ({
    user_id = '',
    name = '',
    email = '',
    oid = '',
    image = null,
    tokenId = '',
    status = '',
    site_tour = '',
    action = null,
    canceled = false,
    start_date = null,
    canceled_at = null,
    current_period_start = null,
    current_period_end = null,
    book_tokens = 0,
    script_tokens = 0,
    extra_book_tokens = 0,
    extra_script_tokens = 0,
    promo_book_tokens = 0,
    promo_script_tokens = 0,
    promo_expiration_date = null,
    custom_script_template = null,
    custom_book_template = null,
    show_chat = false,
    company_id = '',
    company_name = '',
    isIndividualUser = true
  }) => {
    console.log('UserDetailsContext: updateUserDetails called with:', { email, name, user_id });
    setUserDetails({
      ...userDetails,
      user_id,
      name,
      email,
      oid,
      image,
      tokenId,
      status,
      site_tour,
      action,
      canceled,
      start_date,
      canceled_at,
      current_period_start,
      current_period_end,
      book_tokens,
      script_tokens,
      extra_book_tokens,
      extra_script_tokens,
      promo_book_tokens,
      promo_script_tokens,
      promo_expiration_date,
      custom_book_template,
      custom_script_template,
      show_chat,
      company_id,
      company_name,
      isIndividualUser
    });
  };

  const isPromoValid = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date() <= new Date(expirationDate);
  };

  const updateUserTokens = (book_tokens, script_tokens) => {
    setUserDetails((prev) => {
      const next = { ...prev };
      const promoValid = isPromoValid(prev.promo_expiration_date);

      if (book_tokens === 0) {
        // Deducting script tokens
        let remaining = Math.abs(script_tokens);
        if (promoValid && prev.promo_script_tokens > 0) {
          const deduct = Math.min(remaining, prev.promo_script_tokens);
          next.promo_script_tokens = prev.promo_script_tokens - deduct;
          remaining -= deduct;
        }
        if (remaining > 0 && prev.extra_script_tokens > 0) {
          const deduct = Math.min(remaining, prev.extra_script_tokens);
          next.extra_script_tokens = prev.extra_script_tokens - deduct;
          remaining -= deduct;
        }
        if (remaining > 0) {
          next.script_tokens = prev.script_tokens - remaining;
        }
      } else {
        // Deducting book tokens
        let remaining = Math.abs(book_tokens);
        if (promoValid && prev.promo_book_tokens > 0) {
          const deduct = Math.min(remaining, prev.promo_book_tokens);
          next.promo_book_tokens = prev.promo_book_tokens - deduct;
          remaining -= deduct;
        }
        if (remaining > 0 && prev.extra_book_tokens > 0) {
          const deduct = Math.min(remaining, prev.extra_book_tokens);
          next.extra_book_tokens = prev.extra_book_tokens - deduct;
          remaining -= deduct;
        }
        if (remaining > 0) {
          next.book_tokens = prev.book_tokens - remaining;
        }
      }

      return next;
    });
  };

  return (
    // eslint-disable-next-line react/jsx-filename-extension
    <UserDetailsContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{ userDetails, updateUserTokens, updateUserDetails }}>
      {children}
    </UserDetailsContext.Provider>
  );
}
